import { ButtonGroup } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import { getConfirmation, setConfirmation } from "../../api/confirmation";
import { getSchedules } from "../../api/schedules";
import { userAtom } from "../../store/user";
import ScheduleEdit from "./ScheduleEdit";

export default function Calendar(props) {
  const { updateSummary } = props;
  const user = useRecoilValue(userAtom);

  const [pivot, setPivot] = useState(moment().startOf("month"));

  const { data, refetch, isSuccess } = useQuery({
    queryKey: ["schedule", pivot],
    queryFn: () => getSchedules(pivot.year(), pivot.month() + 1),
    throwOnError: true,
  });

  const { data: confirmation, refetch: refetchConfirmation } = useQuery({
    queryKey: ["confirmation", pivot],
    queryFn: () => getConfirmation(pivot.year(), pivot.month() + 1),
    throwOnError: true,
  });

  const startWorkTime = user.department.start_work_time.substring(0, 5);
  const endWorkTime = user.department.end_work_time.substring(0, 5);
  const scheduleMap = new Map();
  if (isSuccess) {
    const byDate = Map.groupBy(data, (e) => e.date);
    byDate.forEach((v, date, m) => {
      const scheduleLines = [];
      scheduleMap.set(date, scheduleLines);

      v.forEach((s, schedule_type, m) => {
        const is_task = s.source === 3;
        scheduleLines.push({
          id: s.id,
          updated_at: s.updated_at,
          start_time: (s.start_time ?? startWorkTime).substring(0, 5),
          end_time: (s.end_time ?? endWorkTime).substring(0, 5),
          is_all_day: s.is_all_day,
          is_negative: s.is_negative,
          is_task: is_task,
          name: is_task ? s.task.name : s.schedule_type.name,
          schedule_type_id: is_task ? 0 : s.schedule_type.id,
          schedule_type_order: is_task ? 0 : s.schedule_type.display_order,
          working_hours: s.working_hours,
          source: s.source,
          is_settled: s.is_settled,
          note: s.note,
          bg_color: s.schedule_type.bg_color,
          fg_color: s.schedule_type.fg_color,
        });
      });
      const comp = (a, b) => {
        return a < b ? -1 : a > b ? 1 : 0;
      };
      scheduleLines.sort((a, b) => {
        if (!a.is_negative && b.is_negative) {
          return -1;
        }
        if (a.is_negative && !b.is_negative) {
          return 1;
        }
        if (a.is_negative && b.is_negative) {
          return comp(a.schedule_type_order, b.schedule_type_order);
        }
        if (a.start_time < startWorkTime && b.start_time >= startWorkTime) {
          return 1;
        }
        if (a.start_time >= startWorkTime && b.start_time < startWorkTime) {
          return -1;
        }
        return comp(a.start_time, b.start_time);
      });
    });
    //console.log(scheduleMap);
  }

  // form dialog
  const [open, setOpen] = React.useState(false);
  const [scheduleData, setScheduleData] = React.useState(null);
  const [scheduleDate, setScheduleDate] = React.useState(null);
  const [scheduleByDate, setScheduleByDate] = React.useState(null);

  const handleClickOpen = (date, s) => {
    setScheduleDate(date);
    setScheduleData(s);
    setScheduleByDate(scheduleMap.get(date));
    setOpen(true);
  };
  const handleClose = async () => {
    setOpen(false);
    await refetch();
    updateSummary();
  };
  // form dialog

  const handleShowConfirm = () => {
    if (!confirmation) {
      return;
    }
    if (!confirmation.confirmed) {
      // eslint-disable-next-line no-restricted-globals
      if (confirm("この月の予定を確定しますか？")) {
        setConfirmation({
          year: pivot.year(),
          month: pivot.month() + 1,
          confirmed: true,
        }).then(async () => {
          await refetchConfirmation();
          alert("確定しました");
        });
      }
    } else {
      // eslint-disable-next-line no-restricted-globals
      if (confirm("この月の予定を確定解除しますか？")) {
        setConfirmation({
          year: pivot.year(),
          month: pivot.month() + 1,
          confirmed: false,
        }).then(async () => {
          await refetchConfirmation();
          alert("確定解除しました");
        });
      }
    }
  };

  const handleThisMonth = async () => {
    setPivot(moment().startOf("month").clone());
    await refetch();
  };

  const handleNextMonth = async () => {
    setPivot(pivot.startOf("month").add("month", 1).clone());
    await refetch();
  };

  const handlePrevMonth = async () => {
    setPivot(pivot.startOf("month").add("month", -1).clone());
    await refetch();
  };

  const canAllDay = (byDate, isNew) => {
    if (!byDate) {
      return true;
    }

    return byDate.length === (isNew ? 0 : 1);
  };

  const regulateTime = (dateString, timeString, isEnd = false) => {
    const time = moment(`${dateString} ${timeString}`);
    const startWorkTime = moment(
      `${dateString} ${user.department.start_work_time}`,
    );

    if (time.isBefore(startWorkTime) || (isEnd && time.isSame(startWorkTime))) {
      time.add(1, "days");
    }

    return time;
  };

  const checkRegister = (target, byDate, exclude) => {
    //console.log('target:' + JSON.stringify(target));

    const date = target.date;
    const target_start_time = regulateTime(date, target.start_time);
    const target_end_time = regulateTime(date, target.end_time, true);

    //console.log('target_start_time:', target_start_time);
    //console.log('target_end_time:', target_end_time);
    if (target_start_time.isSameOrAfter(target_end_time)) {
      return "終了時刻は開始時刻以降で指定してください";
    }

    if (!byDate) {
      return null;
    }

    for (const v of byDate) {
      if (!target.is_negative && v === exclude) {
        continue;
      }

      if (
        target.is_negative &&
        v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に登録されています";
      }
      if (v.is_all_day) {
        return "予定をこれ以上追加できません";
      }
      if (
        target.is_negative &&
        !v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に登録されている種別を割当て不可にできません。";
      }
      if (
        !target.is_negative &&
        v.is_negative &&
        target.schedule_type_id === v.schedule_type_id
      ) {
        return "既に割当て不可として登録されています。";
      }

      const start_time = regulateTime(date, v.start_time);
      const end_time = regulateTime(date, v.end_time, true);

      if (
        target_start_time.isBefore(end_time) &&
        target_end_time.isAfter(start_time)
      ) {
        return "他の予定と時間が重複しています";
      }
    }

    return null;
  };

  const now = pivot.clone();
  const today = moment().format("YYYY-MM-DD");
  const start = now.startOf("month"); // 月初めの日付
  const end = moment(now).endOf("month"); // 月終わりの日付
  const calendar = [];

  const cellStyles = {
    borderLeft: 1,
    borderTop: 1,
    borderColor: "#999",
    width: "100%",
    paddingTop: "4px",
    paddingBottom: "4px",
  };

  [...Array(Number(start.format("d")))].map((_, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
    calendar.push(<Box sx={{ ...cellStyles }} key={i} />),
  );

  while (start.unix() <= end.unix()) {
    const date = start.format("YYYY-MM-DD");
    calendar.push(
      <Box
        sx={{
          ...cellStyles,
          aspectRatio: "1 / 1",
          paddingLeft: "5px",
          paddingRight: "3px",
          overflow: "hidden",
          background: date === today ? "#ffefc9" : "#fff",
        }}
        key={date}
        onClick={() => handleClickOpen(date, null)}
      >
        {start.format("D")}
        {scheduleMap.has(date) &&
          scheduleMap.get(date).map((s, i) => (
            <Box
              sx={{
                paddingTop: "1px",
                paddingLeft: "6px",
                marginBottom: "4px",
                height: "21px",
                fontSize: "13px",
                background: s.is_negative ? "#707070" : s.bg_color,
                outlineWidth: s.is_settled ? 2 : 0,
                outlineStyle: s.is_settled ? "solid" : "none",
                borderRadius: "4px",
                overflow: "hidden",
              }}
              // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                s.source === 1 && handleClickOpen(date, s);
              }}
            >
              {!s.is_negative && (
                <Typography
                  sx={{
                    display: "inline",
                    background: "#707070",
                    color: "white",
                    fontSize: "10px",
                    padding: "2px",
                    marginRight: "3px",
                    verticalAlign: "text-top",
                  }}
                >
                  {s.start_time}
                </Typography>
              )}
              <Typography
                component="span"
                sx={{
                  fontSize: "13px",
                  fontWeight: s.source === 1 ? "normal" : "bold",
                  color: s.is_negative ? "white" : (s.fg_color ?? "#763833"),
                }}
              >
                {s.name + (s.is_negative ? "(不可)" : "")}
              </Typography>
            </Box>
          ))}
      </Box>,
    );
    start.add(1, "days");
  }

  [...Array((7 - (calendar.length % 7)) % 7)].map((_, i) =>
    // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
    calendar.push(<Box sx={{ ...cellStyles }} key={i + calendar.length} />),
  );

  return (
    <>
      <ScheduleEdit
        key={Date.now()}
        isNew={scheduleData === null}
        date={scheduleDate}
        obj={scheduleData}
        byDate={scheduleByDate}
        open={open}
        onClose={handleClose}
        canAllDay={canAllDay}
        checkRegister={checkRegister}
      />

      <Box
        sx={{
          width: "100%",
          height: "40px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          alignSelf: "stretch",
          marginBottom: "24px",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: "Noto Sans JP",
            fontStyle: "normal",
            fontWeight: "400",
            fontSize: "24px",
            lineHeight: "133.4%",
            color: "rgba(0, 0, 0, 0.6)",
            marginLeft: "12px",
          }}
        >
          {pivot.format("YYYY年M月")}
        </Typography>
        <Stack
          direction="row"
          sx={{
            gap: "8px",
            width: "250px",
            height: "40px",
          }}
        >
          <Button
            variant="contained"
            color={confirmation?.confirmed ? "primary" : "error"}
            size="medium"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              width: "100px",
              height: "40px",
              /* elevation/2 */
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
            }}
            onClick={handleShowConfirm}
          >
            <Typography
              sx={{
                color: "var(--primary-contrast, #FFF)",
                /* button/medium */
                fontFamily: "Noto Sans JP",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "24px" /* 171.429% */,
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              {confirmation?.confirmed ? "確定済" : "未確定"}
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "8px 16px",
              width: "62px",
              height: "40px",
              background: "#21456A",
              /* elevation/2 */
              boxShadow:
                "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              borderRadius: "6px",
            }}
            onClick={handleThisMonth}
          >
            <Typography
              sx={{
                color: "var(--primary-contrast, #FFF)",
                /* button/medium */
                fontFamily: "Noto Sans JP",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "500",
                lineHeight: "24px" /* 171.429% */,
                letterSpacing: "1.25px",
                textTransform: "uppercase",
              }}
            >
              今日
            </Typography>
          </Button>
          <ButtonGroup
            color="primary"
            orientation="horizontal"
            variant="contained"
            sx={{
              borderRadius: "var(--borderRadius, 6px)",
              background: "var(--primary-main, #21456A)",
              /* elevation/2 */
              boxShadow:
                "0px 3px 1px -2px rgba(0, 0, 0, 0.20), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)",
            }}
          >
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handlePrevMonth}
            >
              <svg
                role="img"
                aria-label="Previous month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M14.71 6.71047C14.32 6.32047 13.69 6.32047 13.3 6.71047L8.70998 11.3005C8.31998 11.6905 8.31998 12.3205 8.70998 12.7105L13.3 17.3005C13.69 17.6905 14.32 17.6905 14.71 17.3005C15.1 16.9105 15.1 16.2805 14.71 15.8905L10.83 12.0005L14.71 8.12047C15.1 7.73047 15.09 7.09047 14.71 6.71047Z"
                  fill="white"
                />
              </svg>
            </Button>
            <Button
              sx={{
                padding: "8px",
                width: "40px",
                height: "40px",
                borderRadius: "4px",
              }}
              onClick={handleNextMonth}
            >
              <svg
                role="img"
                aria-label="Next month"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9.29006 6.70978C8.90006 7.09978 8.90006 7.72978 9.29006 8.11978L13.1701 11.9998L9.29006 15.8798C8.90006 16.2698 8.90006 16.8998 9.29006 17.2898C9.68006 17.6798 10.3101 17.6798 10.7001 17.2898L15.2901 12.6998C15.6801 12.3098 15.6801 11.6798 15.2901 11.2898L10.7001 6.69978C10.3201 6.31978 9.68006 6.31978 9.29006 6.70978Z"
                  fill="white"
                />
              </svg>
            </Button>
          </ButtonGroup>
        </Stack>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridAutoFlow: "row",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
          borderRight: 1,
          borderBottom: 1,
          borderColor: "#999",
        }}
      >
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#f00" }}>日</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>月</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>火</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>水</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>木</Box>
        <Box sx={{ ...cellStyles, textAlign: "center" }}>金</Box>
        <Box sx={{ ...cellStyles, textAlign: "center", color: "#00f" }}>土</Box>
        {calendar}
      </Box>
    </>
  );
}
